import { useEffect, useRef, useState } from "react";
import useWebTracking from "@/hooks/useWebTracking";
import { Link } from "react-router-dom";
import {
    ShieldCheck,
    Sparkles,
    QrCode,
    LockKeyhole,
    ArrowRight,
    Check,
    Zap,
    Users,
    FileBadge,
    Building2,
    Camera,
    HeartHandshake,
    Trophy,
    MessageSquare,
    BookOpen,
    Share2,
    LifeBuoy,
    Rocket,
    UserCheck,
    CreditCard,
    ClipboardCheck,
    Palette,
    Star,
    Fingerprint,
    Eye,
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import PlatformSupportChat from "@/components/PlatformSupportChat";
import OrganizationJsonLd from "@/components/OrganizationJsonLd";
import api from "@/lib/api";
import { inr } from "@/lib/constants";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import useSiteContent from "@/hooks/useSiteContent";
import usePlatformStats from "@/hooks/usePlatformStats";
import useReveal from "@/hooks/useReveal";

const LANDING_DEFAULTS = {
    landing_hero: {
        eyebrow: "AI-refined · Doctor-approved · Publish-ready",
        headline_l1: "Build Your Professional Identity.",
        headline_l2: "Grow Your Practice.",
        headline_l3: "Leave a Legacy.",
        subheading: "A verified public presence at /dr/<your-name> — refined by AI, approved by admins and by you.",
        cta_primary: "Create Your Doctor Digital Identity",
        cta_secondary: "See packages",
    },
};

const MODULES = [
    { code: "A", name: "Digital Identity", desc: "Verified profile, slug URL, credentials.", icon: FileBadge },
    { code: "B", name: "Brand Building", desc: "Personal bio, brand colors, storytelling.", icon: Sparkles },
    { code: "C", name: "Patient Info", desc: "Consultation hours, fees, insurance.", icon: Users },
    { code: "D", name: "Educational Content", desc: "Articles, condition guides, videos.", icon: BookOpen },
    { code: "E", name: "Social Media", desc: "Cross-platform composer & GMB checklist.", icon: Share2 },
    { code: "F", name: "Achievements", desc: "Awards, certifications, milestones.", icon: Trophy },
    { code: "G", name: "Reputation", desc: "Reviews, testimonials, sentiment.", icon: MessageSquare },
    { code: "H", name: "AI Content Engine", desc: "Refined bios, SEO, FAQ, drafts.", icon: Zap },
    { code: "I", name: "Media & Gallery", desc: "Photos, videos, 3D tour, reels.", icon: Camera },
    { code: "J", name: "Patient Services", desc: "Enquiry, telemedicine, portal.", icon: HeartHandshake },
    { code: "K", name: "Social Impact", desc: "Camps, CSR, impact metrics.", icon: HeartHandshake },
    { code: "L", name: "Documents", desc: "CV, brochures, research repository.", icon: FileBadge },
    { code: "M", name: "Security & Data", desc: "Encryption, RBAC, audit logs.", icon: LockKeyhole },
    { code: "N", name: "Legacy", desc: "Timeline, mentorship, memorial page.", icon: Building2 },
    { code: "O", name: "Continuous Service", desc: "Success, refresh, strategy.", icon: LifeBuoy },
];

const HOW_STEPS = [
    { n: "01", icon: UserCheck, title: "Sign up & verify", body: "Register with your medical registration number, verify OTP, and choose a package." },
    { n: "02", icon: ClipboardCheck, title: "Guided onboarding", body: "Complete 8 structured steps: identity, education, experience, clinics, credentials." },
    { n: "03", icon: Sparkles, title: "AI content refinement", body: "Our grounded AI writes bios, SEO meta, and FAQs — never fabricates, always cites your data." },
    { n: "04", icon: ShieldCheck, title: "Admin + doctor approval", body: "Every published word passes admin review and requires your explicit doctor approval." },
    { n: "05", icon: Rocket, title: "Publish & grow", body: "Your public identity goes live at /dr/<your-name> with QR card, SEO, and social kit." },
];

const TEMPLATE_SWATCHES = [
    // 3 NEW-LAYOUT templates first — showcase the 4 new layout engines
    { name: "Editorial Spread · Navy", code: "editorial_spread_navy", family: "Editorial", tier: 2, colors: ["#123B5D", "#0F766E", "#F59E0B"], isNew: true, layout: "Editorial Spread" },
    { name: "Bento Cards · Teal", code: "bento_cards_teal", family: "Modern", tier: 3, colors: ["#0F5C56", "#14B8A6", "#F59E0B"], isNew: true, layout: "Bento Cards" },
    { name: "Journey First · Teal", code: "timeline_journey_teal", family: "Editorial", tier: 3, colors: ["#0F766E", "#123B5D", "#F59E0B"], isNew: true, layout: "Journey First" },
    { name: "Typographic Minimal · Ink", code: "type_minimal_ink", family: "Minimal", tier: 4, colors: ["#111827", "#0F766E", "#F5F8FA"], isNew: true, layout: "Typographic" },
    // Flagship variants (unchanged legacy templates)
    { name: "DDIP Verified Pro — Navy", code: "ddip_verified_pro", family: "Flagship", tier: 1, colors: ["#0B1B3A", "#2563EB", "#F59E0B"] },
    { name: "DDIP Verified Pro — Purple", code: "ddip_purple", family: "Flagship", tier: 1, colors: ["#3B0764", "#8B5CF6", "#F59E0B"] },
    { name: "Classic Navy", code: "classic_navy", family: "Classic", tier: 1, colors: ["#0F1B2D", "#94A3B8", "#DC2626"] },
    { name: "Modern Teal", code: "modern_teal", family: "Modern", tier: 2, colors: ["#0F766E", "#123B5D", "#F59E0B"] },
];

const FAQ = [
    { q: "Is this a diagnostic or emergency medical service?", a: "No. Doctor Digital Identity is a professional identity, brand, and content platform for practicing doctors. It does not provide clinical diagnosis, treatment guarantees, or emergency services." },
    { q: "Can the AI invent qualifications or awards?", a: "Never. Our AI pipeline is bound by a strict no-fabrication rule. Missing sections are explicitly flagged 'Information Required' and returned to you for completion — nothing is invented." },
    { q: "How do payments work?", a: "Each package is paid in two 50% instalments — an advance to begin onboarding and the balance before publication. GST invoices are generated automatically." },
    { q: "Do I have final approval over my public site?", a: "Yes. Every profile passes admin review first, then requires your explicit doctor approval before it can move to publication. You can request changes at any step." },
    { q: "Which countries are supported?", a: "The platform is India-first, with support for multi-language profiles. International rollout is on the roadmap." },
    { q: "Can I upgrade my package later?", a: "Yes. Upgrades are prorated and unlock additional feature modules immediately after payment." },
    { q: "How is my data secured?", a: "Passwords are bcrypt-hashed, sessions use short-lived JWTs, tenant isolation is enforced at every query, and every sensitive action is captured in an immutable audit log." },
];

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function BrowserFrame({ src, label = "dr/anaya-mehta" }) {
    return (
        <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/10 bg-white" data-testid="hero-browser-frame">
            <div className="flex items-center gap-1.5 bg-[#EEF1F6] px-4 py-3 border-b border-black/5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
                <div className="flex-1 flex items-center justify-center">
                    <span className="text-[11px] font-mono text-[#0F1B2D]/60 bg-white/80 rounded px-3 py-0.5 border border-black/5">
                        doctorsprofile.in{`/${label}`}
                    </span>
                </div>
                <span className="w-14" />
            </div>
            <img src={src} alt="Live doctor digital identity preview" className="w-full block" data-testid="hero-browser-screenshot" />
        </div>
    );
}

function PhoneFrame({ src }) {
    return (
        <div className="w-[190px] rounded-[36px] bg-[#0F1B2D] p-2 shadow-2xl shadow-black/50 ring-1 ring-white/10" data-testid="hero-phone-frame">
            <div className="rounded-[28px] overflow-hidden relative bg-white">
                <div className="absolute top-1 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-[#0F1B2D] z-10" />
                <img src={src} alt="Mobile view of doctor digital identity" className="w-full block" />
            </div>
        </div>
    );
}

function TemplateCard({ tpl }) {
    const [primary, secondary, accent] = tpl.colors;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const wrapRef = useRef(null);
    const [near, setNear] = useState(false);
    useEffect(() => {
        if (!wrapRef.current) return;
        const io = new IntersectionObserver(
            (entries) => setNear(entries[0].isIntersecting),
            { rootMargin: "500px" },
        );
        io.observe(wrapRef.current);
        return () => io.disconnect();
    }, []);
    const iframeSrc = near && tpl.code
        ? `${base}/api/public/dr/demo-anaya-mehta?template_preview=${encodeURIComponent(tpl.code)}&compact=1`
        : "about:blank";
    return (
        <div className="ddi-card overflow-hidden hover:-translate-y-1 transition-transform relative" data-testid={`tpl-card-${tpl.family.toLowerCase()}`}>
            {tpl.isNew && (
                <span className="absolute top-2 left-2 z-10 bg-gradient-to-r from-[#D9A441] to-[#B8862B] text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-sm tracking-wider" data-testid={`landing-tpl-new-${tpl.code}`}>
                    NEW LAYOUT
                </span>
            )}
            {/* Real live preview iframe scaled 22% — same technique as the doctor picker */}
            <div ref={wrapRef} className="relative aspect-[4/3] overflow-hidden" style={{ background: primary }}>
                <iframe
                    src={iframeSrc}
                    title={`Preview ${tpl.name}`}
                    loading="lazy"
                    sandbox="allow-same-origin allow-scripts"
                    scrolling="no"
                    style={{
                        width: "1440px", height: "1080px",
                        border: 0,
                        transform: "scale(0.22)",
                        transformOrigin: "top left",
                        pointerEvents: "none",
                        background: "#fff",
                    }}
                    data-testid={`landing-tpl-iframe-${tpl.code || tpl.family}`}
                />
            </div>
            <div className="p-4 flex items-center justify-between">
                <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[#123B5D] truncate">{tpl.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50 mt-0.5">
                        {tpl.layout ? <span className="text-[#B8862B] font-semibold">{tpl.layout}</span> : tpl.family}
                        {" · Tier "}{tpl.tier}+
                    </div>
                </div>
                <div className="flex gap-1 shrink-0">
                    {tpl.colors.map((c) => (
                        <span key={c} className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10" style={{ background: c }} />
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Landing page                                                       */
/* ------------------------------------------------------------------ */

export default function Landing() {
    useWebTracking();
    const [packages, setPackages] = useState([]);
    const { content } = useSiteContent(LANDING_DEFAULTS);
    const { stats } = usePlatformStats();
    const hero = content.landing_hero || LANDING_DEFAULTS.landing_hero;
    const trust = content.landing_trust || null;
    const what = content.landing_what || null;
    const modIntro = content.landing_modules_intro || null;
    const modules = content.landing_modules || null;
    const howIntro = content.landing_how_intro || null;
    const howSteps = content.landing_how_steps || null;
    const aiSec = content.landing_ai || null;
    const tplIntro = content.landing_templates_intro || null;
    const pkgIntro = content.landing_packages_intro || null;
    const qrSec = content.landing_qr || null;
    const secIntro = content.landing_security_intro || null;
    const finalCta = content.landing_final_cta || null;
    const sections = content.landing_sections || null;
    const showSection = (id) => !sections || (sections.find((s) => s.id === id)?.visible !== false);
    useEffect(() => {
        api.get("/packages")
            .then((r) => setPackages(r.data))
            .catch(() => {});
    }, []);
    // Kick off scroll-reveal animations on every [data-reveal] element after
    // packages/site-content resolve (which pushes new sections into the DOM).
    useReveal();
    useEffect(() => {
        // Re-observe when packages arrive since new nodes render then.
        const t = setTimeout(() => {
            document.querySelectorAll("[data-reveal]:not(.is-revealed), [data-reveal-up]:not(.is-revealed)").forEach((el) => {
                const rect = el.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.9) el.classList.add("is-revealed");
            });
        }, 200);
        return () => clearTimeout(t);
    }, [packages.length]);

    return (
        <div className="min-h-screen bg-white text-[#17212B]">
            <OrganizationJsonLd />

            {/* ============================= HERO ============================= */}
            <div className="ddi-hero-cinema relative">
                <MarketingNav variant="dark" />
                <section className="relative overflow-hidden pb-24 lg:pb-32">
                    <div className="absolute inset-0 ddi-grid-overlay opacity-20 pointer-events-none" />
                    {/* Floating accent dots to break the gradient monotony */}
                    <div className="absolute top-24 left-[18%] w-2 h-2 rounded-full bg-[#5EEAD4] shadow-[0_0_40px_rgba(94,234,212,0.9)] ddi-orb-float pointer-events-none" style={{ animationDelay: "0s" }} />
                    <div className="absolute top-48 right-[22%] w-1.5 h-1.5 rounded-full bg-[#7EB1FC] shadow-[0_0_32px_rgba(126,177,252,0.9)] ddi-orb-float pointer-events-none" style={{ animationDelay: "2.4s" }} />
                    <div className="absolute bottom-28 right-[12%] w-2 h-2 rounded-full bg-[#F59E0B] shadow-[0_0_40px_rgba(245,158,11,0.7)] ddi-orb-float pointer-events-none" style={{ animationDelay: "4.1s" }} />

                    <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center relative z-10">
                        <div className="lg:col-span-6 text-white animate-fade-up">
                            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur-sm" data-testid="hero-badge">
                                <Sparkles size={14} className="text-[#5EEAD4]" />
                                {hero.eyebrow || "AI-refined · Doctor-approved · Publish-ready"}
                            </div>
                            <h1 data-reveal className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.98]" data-testid="hero-title">
                                {hero.headline_l1 || hero.heading || "Build Your Professional Identity."}
                                {(hero.headline_l2 || !hero.heading) && (
                                    <span className="block bg-gradient-to-r from-[#7EB1FC] via-[#5EEAD4] to-[#7EB1FC] bg-clip-text text-transparent">
                                        {hero.headline_l2 || "Grow Your Practice."}
                                    </span>
                                )}
                                {(hero.headline_l3 || !hero.heading) && (
                                    <span className="block">{hero.headline_l3 || "Leave a Legacy."}</span>
                                )}
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
                                {hero.subheading || (
                                    <>A verified public presence at <span className="font-mono text-white/95">/dr/&lt;your-name&gt;</span> — refined by AI, approved by admins and by you, with a package tailored to how much authority you want to project.</>
                                )}
                            </p>
                            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <Link to="/signup" className="ddi-teal-btn text-base inline-flex items-center gap-2" data-testid="hero-primary-cta">
                                    {hero.cta_primary || "Create Your Doctor Digital Identity"}
                                    <ArrowRight size={16} />
                                </Link>
                                <a href="/api/public/dr/demo-anaya-mehta?demo=1" target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition"
                                   data-testid="hero-view-demo-cta">
                                    View Live Demo
                                    <ArrowRight size={14} />
                                </a>
                                <Link to="/identity-score" className="text-sm font-semibold text-white/80 hover:text-white inline-flex items-center gap-1.5" data-testid="hero-score-cta">
                                    Check FREE Identity Score <ArrowRight size={14} />
                                </Link>
                            </div>
                            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md text-sm">
                                <div><div className="text-2xl font-bold text-white ddi-count-up" data-testid="stat-features">{stats.total_features}</div><div className="text-white/50">Curated features</div></div>
                                <div><div className="text-2xl font-bold text-white ddi-count-up" data-testid="stat-templates">{stats.total_templates}</div><div className="text-white/50">Design templates</div></div>
                                <div><div className="text-2xl font-bold text-white ddi-count-up" data-testid="stat-packages">{stats.total_packages}</div><div className="text-white/50">Package tiers</div></div>
                            </div>
                        </div>

                        <div className="lg:col-span-6 relative animate-fade-up" data-testid="hero-mockup-group">
                            <div className="ddi-tilt">
                                <BrowserFrame src="/hero-flagship-desktop.png" label="dr/demo-anaya-mehta" />
                            </div>
                            <div className="hidden sm:block absolute -bottom-14 -right-6 lg:right-2 ddi-tilt">
                                <PhoneFrame src="/hero-flagship-mobile.png" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* ============================= TRUST STRIP ============================= */}
            {showSection("trust") && (
            <section className="bg-white border-b border-[#123B5D]/10">
                <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {(trust || [
                        { label: "Grounded AI", value: "Zero fabrication" },
                        { label: "Approvals", value: "Admin + Doctor" },
                        { label: "Public URL", value: "/dr/<your-name>" },
                        { label: "Payments", value: "50% + 50% GST" },
                    ]).map((t, i) => {
                        const icons = [ShieldCheck, UserCheck, Fingerprint, CreditCard];
                        const Icon = icons[i % icons.length];
                        return (
                            <div key={t.label + i} className="flex items-start gap-3" data-testid={`trust-${(t.label || '').toLowerCase().replace(/ /g, '-')}`}>
                                <div className="h-10 w-10 rounded-lg bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center shrink-0"><Icon size={18} /></div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#17212B]/50">{t.label}</div>
                                    <div className="text-[15px] font-semibold text-[#123B5D]">{t.value || t.val}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
            )}

            {/* ============================= LIVE DOCTOR SITES SHOWCASE ============================= */}
            {showSection("showcase") && (
            <section className="relative bg-[#F5F8FA] overflow-hidden" data-testid="live-showcase-section">
                <div className="absolute -top-28 -left-20 w-[480px] h-[480px] rounded-full bg-[#2563EB]/8 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-28 -right-20 w-[520px] h-[520px] rounded-full bg-[#0F766E]/8 blur-3xl pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative">
                    <div className="max-w-2xl" data-reveal-up>
                        <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Live sites</div>
                        <h2 className="mt-3 text-3xl lg:text-5xl font-bold tracking-tight text-[#123B5D] leading-[1.05]">
                            Real doctors.<br/>
                            <span className="bg-gradient-to-r from-[#0F766E] to-[#2563EB] bg-clip-text text-transparent">Real published identities.</span>
                        </h2>
                        <p className="mt-4 text-[15px] text-[#17212B]/70 leading-relaxed">
                            Click any card to open the live site. Every one of these is a real published Doctor Digital Identity — AI-refined, admin-reviewed, doctor-approved.
                        </p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="live-showcase-grid">
                        {[
                            // D-HOTFIX-03: three REAL distinct demo doctors (all seeded + published
                            // in milestone B3). Previously all three cards pointed at demo-anaya-mehta
                            // with cosmetic `?variant=` queries which fabricated doctor identities and
                            // sent visitors to the same page three times. Now each card opens its own
                            // real published Doctor Digital Identity.
                            { slug: "demo-anaya-mehta",  name: "Dr. Anaya Mehta",   spec: "Dermatology",       tpl: "Flagship Navy",  accent: "#2563EB" },
                            { slug: "demo-suresh-iyer",  name: "Dr. Suresh Iyer",   spec: "Orthopedic Surgery", tpl: "Editorial Teal", accent: "#0F766E" },
                            { slug: "demo-priya-nair",   name: "Dr. Priya Nair",    spec: "Pediatrics",         tpl: "Bento Cards",    accent: "#F59E0B" },
                        ].map((d, i) => (
                            <a
                                key={d.slug + i}
                                href={`/api/public/dr/${d.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group ddi-showcase-card block rounded-2xl bg-white border border-[#123B5D]/10 overflow-hidden shadow-[0_14px_40px_-20px_rgba(15,27,45,0.25)]"
                                data-reveal-up
                                data-testid={`showcase-card-${i}`}
                                data-track={`showcase-card-click:${d.slug}:${d.tpl.replace(/\s+/g, "-").toLowerCase()}`}
                                style={{ transitionDelay: `${i * 90}ms` }}
                            >
                                {/* Device-framed browser preview using the hero screenshot */}
                                <div className="relative aspect-[4/3] bg-gradient-to-br from-[#0F1B2D] to-[#1E3A5F] overflow-hidden">
                                    <div className="absolute inset-6 rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/20">
                                        <div className="h-6 bg-[#0F1B2D] flex items-center gap-1.5 px-3 border-b border-white/10">
                                            <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
                                            <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
                                            <span className="h-2 w-2 rounded-full bg-[#28C840]" />
                                            <span className="ml-3 text-[9px] text-white/50 font-mono truncate">doctorsprofile.in/dr/{d.slug}</span>
                                        </div>
                                        <img
                                            src="/hero-flagship-desktop.png"
                                            alt={`${d.name} website preview`}
                                            className="w-full h-full object-cover object-top transition-transform duration-[1400ms] group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2 py-0.5 text-[10px] font-semibold text-[#123B5D]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#15803D]" /> LIVE
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <div className="text-[11px] uppercase tracking-widest text-[#17212B]/50 font-semibold">{d.spec}</div>
                                            <div className="mt-1 text-[17px] font-bold text-[#123B5D] group-hover:text-[#0F766E] transition-colors">
                                                {d.name}
                                            </div>
                                        </div>
                                        <div className="h-9 w-9 rounded-full flex items-center justify-center transition-colors" style={{ background: `${d.accent}15`, color: d.accent }}>
                                            <ArrowRight size={16} />
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-center justify-between text-xs">
                                        <span className="inline-flex items-center gap-1.5 text-[#17212B]/60">
                                            <Palette size={12} /> {d.tpl}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 text-[#0F766E] font-semibold">
                                            Visit <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                    <div className="mt-10 flex items-center justify-center">
                        <Link to="/demos" className="inline-flex items-center gap-2 text-sm font-semibold text-[#123B5D] hover:text-[#0F766E] transition" data-testid="showcase-view-all">
                            Explore all 172 design templates <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= WHAT IS IT ============================= */}
            {showSection("what") && (
            <section className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">{what?.eyebrow || "What it is"}</div>
                        <h2 data-reveal data-reveal-delay="40" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">
                            {what?.heading || "A single verified home for your professional identity."}
                        </h2>
                    </div>
                    <div className="lg:col-span-7 text-[#17212B]/80 leading-relaxed text-base lg:text-lg">
                        <p>{what?.body_p1 || "Doctor Digital Identity is a curated, verified profile — refined by AI, reviewed by admins, and approved by you — hosted at a public URL you own. It is not a diagnostic platform, hospital directory, or emergency service."}</p>
                        <p className="mt-4">{what?.body_p2 || "Every piece of content is grounded in facts you provide. If a section can't be sourced from your input, the platform explicitly flags it as \"Information Required\" — never fabricated."}</p>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= MODULES ============================= */}
            {showSection("modules") && (
            <section id="modules" className="bg-[#F5F8FA] border-y border-[#123B5D]/10">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold" data-testid="modules-features-strip">
                            {(modIntro?.eyebrow_prefix || "15 modules ·")} {stats.total_features} features
                        </div>
                        <h2 data-reveal data-reveal-delay="80" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">
                            {modIntro?.heading || "Everything a doctor's digital presence needs."}
                        </h2>
                        <p className="mt-4 text-[#17212B]/70 max-w-2xl">
                            {modIntro?.body || "Feature modules are unlocked by package tier. Every module is DB-driven — the entitlement engine, not the code, decides what appears in your workspace."}
                        </p>
                    </div>
                    <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {(modules || MODULES).map((m) => {
                            const iconLookup = { A: FileBadge, B: Sparkles, C: Users, D: BookOpen, E: Share2, F: Trophy, G: MessageSquare, H: Zap, I: Camera, J: HeartHandshake, K: HeartHandshake, L: FileBadge, M: LockKeyhole, N: Building2, O: LifeBuoy };
                            const Icon = m.icon || iconLookup[m.code] || FileBadge;
                            return (
                                <div key={m.code + m.name} className="ddi-card p-4 hover:border-[#2563EB]/40 transition-colors" data-testid={`module-${m.code}`}>
                                    <div className="h-9 w-9 rounded-lg bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center"><Icon size={16} /></div>
                                    <div className="mt-3 text-[10px] uppercase tracking-widest text-[#17212B]/50">Module {m.code}</div>
                                    <div className="mt-0.5 text-[14px] font-semibold text-[#123B5D]">{m.name}</div>
                                    <div className="mt-1 text-[12px] text-[#17212B]/60 leading-relaxed">{m.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
            )}

            {/* ============================= HOW IT WORKS ============================= */}
            {showSection("how") && (
            <section id="how" className="bg-white">
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">{howIntro?.eyebrow || "How it works"}</div>
                        <h2 data-reveal data-reveal-delay="120" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">{howIntro?.heading || "From sign-up to a published identity in 5 clear steps."}</h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 relative">
                        <div className="hidden lg:block absolute top-14 left-8 right-8 h-px bg-gradient-to-r from-[#2563EB]/40 via-[#0F766E]/40 to-[#2563EB]/40" />
                        {(howSteps || HOW_STEPS).map((s) => {
                            const stepIcons = [UserCheck, ClipboardCheck, Sparkles, ShieldCheck, Rocket];
                            const idx = parseInt((s.n || "0"), 10) - 1;
                            const Icon = s.icon || stepIcons[idx] || Rocket;
                            return (
                                <div key={s.n + (s.title || '')} className="relative ddi-card p-5" data-testid={`how-step-${s.n}`}>
                                    <div className="h-11 w-11 rounded-full bg-white ring-4 ring-[#F5F8FA] flex items-center justify-center text-[#123B5D] shadow-sm relative z-10">
                                        <Icon size={18} />
                                    </div>
                                    <div className="mt-3 text-[10px] uppercase tracking-widest text-[#2563EB] font-semibold">{s.n}</div>
                                    <div className="mt-1 text-[15px] font-bold text-[#123B5D]">{s.title}</div>
                                    <div className="mt-2 text-[13px] text-[#17212B]/70 leading-relaxed">{s.body}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-10 text-center">
                        <Link to="/how-it-works" className="inline-flex items-center gap-1.5 text-[#0F766E] font-semibold hover:underline" data-testid="landing-how-cta">
                            See the full 8-step journey <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= AI SECTION ============================= */}
            {showSection("ai") && (
            <section id="ai" className="bg-gradient-to-br from-[#0F1B2D] via-[#123B5D] to-[#0F1B2D] text-white">
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-6">
                        <div className="text-xs uppercase tracking-widest text-[#5EEAD4] font-semibold">{aiSec?.eyebrow || "AI content engine"}</div>
                        <h2 data-reveal data-reveal-delay="0" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight">{aiSec?.heading || "Grounded AI. Zero fabrication. Always yours to approve."}</h2>
                        <p className="mt-5 text-white/75 leading-relaxed">{aiSec?.body || "Our async pipeline drafts your biography, profile summary, SEO meta, and FAQs from the facts you supply. Missing data is never invented — it's flagged \"Information Required\" and returned for your input."}</p>
                        <div className="mt-8 space-y-3">
                            {(aiSec?.bullets || [
                                "No fabricated qualifications, awards, or affiliations.",
                                "Prompt version + model + input reference stored per job.",
                                "You review and approve every AI-generated section.",
                                "Regenerate any section with one click.",
                            ]).map((t) => (
                                <div key={t} className="flex items-start gap-3 text-sm text-white/85">
                                    <Check size={16} className="text-[#5EEAD4] mt-0.5 shrink-0" />
                                    <span>{t}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="rounded-2xl bg-black/30 backdrop-blur border border-white/10 p-6 shadow-2xl shadow-black/50" data-testid="ai-preview-card">
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/60">
                                <Zap size={13} className="text-[#5EEAD4]" /> AI job · #ai_j_2c8f9a · Completed
                            </div>
                            <div className="mt-4 space-y-4 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">Professional Biography</div>
                                    <div className="mt-1 text-white/90 leading-relaxed">
                                        MD Dermatology (KEM, Mumbai). Twelve years of clinical practice with a focus on evidence-based aesthetic dermatology. Consultant at three multi-specialty clinics across Mumbai.
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">Awards Summary</div>
                                    <div className="mt-1 inline-flex items-center gap-2 rounded-md bg-[#F59E0B]/15 text-[#F59E0B] px-2 py-1 text-[12px] font-mono">
                                        Information Required
                                    </div>
                                    <div className="mt-1 text-[11px] text-white/50">No award records provided in onboarding step 5.</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-white/50">SEO Meta</div>
                                    <div className="mt-1 font-mono text-[12px] text-white/85 leading-relaxed">
                                        Dr. Anaya Mehta, MD Dermatology · Aesthetic dermatology in Mumbai. Book verified appointments and view profile.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= TEMPLATES ============================= */}
            {showSection("templates") && (
            <section id="templates" className="bg-[#F5F8FA] border-y border-[#123B5D]/10">
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">{stats.total_templates} {tplIntro?.eyebrow_prefix || "templates · 7 families"}</div>
                        <h2 data-reveal data-reveal-delay="40" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">{tplIntro?.heading || "Choose a design that fits your practice."}</h2>
                        <p className="mt-4 text-[#17212B]/70 max-w-2xl">
                            {tplIntro?.body || "Every template runs on the same premium Flagship layout engine — QR card, verified badge, stats strip, articles, gallery. What changes is the palette and typography that best matches your specialty."}
                        </p>
                    </div>
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="templates-gallery">
                        {TEMPLATE_SWATCHES.map((t) => (
                            <TemplateCard key={t.name} tpl={t} />
                        ))}
                    </div>
                    <div className="mt-8 text-center">
                        <Link to="/signup" className="inline-flex items-center gap-2 text-[#2563EB] font-semibold hover:underline" data-testid="templates-cta">
                            <Palette size={15} /> {tplIntro?.cta || `Preview all ${stats.total_templates} templates after sign-up`}
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= PACKAGES ============================= */}
            {showSection("packages") && (
            <section id="pricing" className="bg-white">
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">{pkgIntro?.eyebrow || "Packages"}</div>
                        <h2 data-reveal data-reveal-delay="80" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">{pkgIntro?.heading || "Pick the identity tier that matches your ambition."}</h2>
                        <p className="mt-4 text-[#17212B]/70">{pkgIntro?.body || "All prices in ₹ (INR), exclusive of GST. Pay 50% advance to begin onboarding, 50% before publication. AMC billed annually from year 2."}</p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="landing-packages-grid">
                        {packages.map((p) => {
                            const featured = p.code === "professional" || p.code === "premium";
                            return (
                                <div
                                    key={p.code}
                                    className={
                                        "ddi-card p-5 flex flex-col " +
                                        (featured ? "ring-2 ring-[#2563EB] shadow-xl shadow-[#2563EB]/10 relative" : "")
                                    }
                                    data-testid={`landing-pkg-${p.code}`}
                                >
                                    {p.code === "professional" && (
                                        <Badge className="absolute -top-3 left-5 bg-[#2563EB] text-white">Most popular</Badge>
                                    )}
                                    {p.code === "premium" && (
                                        <Badge className="absolute -top-3 left-5 bg-[#0F766E] text-white">Best value</Badge>
                                    )}
                                    <div className="text-xs uppercase tracking-widest text-[#17212B]/50">Tier {p.tier ?? ""}</div>
                                    <div className="mt-1 text-lg font-bold text-[#123B5D]">{p.name}</div>
                                    <div className="mt-3">
                                        <span className="text-3xl font-bold text-[#123B5D]">{inr(p.price_inr)}</span>
                                        <div className="text-[11px] text-[#17212B]/60">one-time · {inr(p.amc_inr)} / yr AMC</div>
                                    </div>
                                    <div className="mt-4 text-[13px] text-[#17212B]/80"><span className="font-bold text-[#123B5D]">{p.feature_count}</span> features unlocked</div>
                                    <ul className="mt-3 space-y-1.5 text-[13px] text-[#17212B]/70 flex-1">
                                        {(p.headline_features || []).slice(0, 3).map((f, i) => (
                                            <li key={i} className="flex items-start gap-2"><Check size={13} className="text-[#0F766E] mt-1 shrink-0" />{f}</li>
                                        ))}
                                    </ul>
                                    <Link to={`/signup?pkg=${p.code}`} className={"mt-5 text-center rounded-full text-sm font-semibold py-2 transition " + (featured ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]" : "border border-[#123B5D] text-[#123B5D] hover:bg-[#123B5D] hover:text-white")} data-testid={`landing-pkg-cta-${p.code}`}>
                                        Choose {p.name}
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-8 text-center">
                        <Link to="/pricing" className="inline-flex items-center gap-1.5 text-[#2563EB] font-semibold hover:underline">
                            {pkgIntro?.compare_cta || "Full feature comparison"} <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= QR / VISITING CARD ============================= */}
            {showSection("qr") && (
            <section className="bg-gradient-to-br from-[#F5F8FA] to-white border-y border-[#123B5D]/10">
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">{qrSec?.eyebrow || "Digital visiting card"}</div>
                        <h2 data-reveal data-reveal-delay="120" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">
                            {qrSec?.heading || "One QR code. Every patient who scans it lands on your verified page."}
                        </h2>
                        <p className="mt-5 text-[#17212B]/70 leading-relaxed">
                            {qrSec?.body || "Print it on prescription slips, WhatsApp-DM it to referrals, add it to your clinic's reception. The QR routes to your public digital identity — refreshed automatically whenever you publish an update."}
                        </p>
                        <ul className="mt-6 space-y-3 text-sm text-[#17212B]/80">
                            {(qrSec?.bullets || ["Ready-to-print vCard, PDF and PNG downloads.",
                              "Share button on public page — WhatsApp, LinkedIn, X, e-mail.",
                              "Every scan is anonymised & captured in your analytics."])
                              .map((t) => (
                                <li key={t} className="flex items-start gap-2"><Check size={15} className="text-[#0F766E] mt-0.5" />{t}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-6 flex justify-center">
                        <div className="rounded-2xl bg-white p-6 shadow-xl border border-[#123B5D]/10 max-w-sm w-full" data-testid="qr-card-mockup">
                            <div className="text-[10px] uppercase tracking-widest text-[#2563EB] font-semibold flex items-center gap-1.5">
                                <ShieldCheck size={11} /> Verified Identity
                            </div>
                            <div className="mt-4 rounded-xl bg-[#F5F8FA] p-4 flex items-center justify-center">
                                <QrCode size={130} className="text-[#0F1B2D]" strokeWidth={1.6} />
                            </div>
                            <div className="mt-5">
                                <div className="text-[15px] font-bold text-[#123B5D]">Dr. Anaya Mehta</div>
                                <div className="text-[12px] text-[#17212B]/60">Dermatology · Mumbai</div>
                                <div className="mt-2 text-[11px] font-mono text-[#0F766E]">/dr/demo-anaya-mehta</div>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-widest text-[#17212B]/50 text-center">
                                <div className="rounded bg-[#F5F8FA] py-1.5">Book</div>
                                <div className="rounded bg-[#F5F8FA] py-1.5">Call</div>
                                <div className="rounded bg-[#F5F8FA] py-1.5">Share</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* ============================= SECURITY ============================= */}
            {showSection("security") && (
            <section id="security" className="bg-white">
                <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">{secIntro?.eyebrow || "Security & data"}</div>
                        <h2 data-reveal data-reveal-delay="0" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">
                            {secIntro?.heading || "Built with tenant isolation, audit logs, and zero shortcuts."}
                        </h2>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                            { icon: LockKeyhole, title: "Bcrypt + short JWTs", body: "Passwords are hashed with bcrypt. Sessions expire quickly and refresh with server-side validation." },
                            { icon: ShieldCheck, title: "Strict tenant isolation", body: "Every query is scoped to your doctor_id server-side. No client-sent IDs are trusted." },
                            { icon: Eye, title: "Immutable audit trail", body: "Approvals, publishing, refunds, and admin actions are captured in an append-only log." },
                            { icon: UserCheck, title: "Dual approval", body: "Admin review AND explicit doctor approval are both mandatory before any content goes public." },
                            { icon: CreditCard, title: "Signed payment webhooks", body: "Only server-verified gateway webhooks can mark a payment as successful — client callbacks are ignored." },
                            { icon: Fingerprint, title: "Verified registration", body: "Your medical registration number is captured at signup and displayed on your public identity." },
                        ].map((s) => {
                            const Icon = s.icon;
                            return (
                                <div key={s.title} className="ddi-card p-5" data-testid={`security-${s.title.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                                    <div className="h-10 w-10 rounded-lg bg-[#0F766E]/10 text-[#0F766E] flex items-center justify-center"><Icon size={18} /></div>
                                    <div className="mt-3 text-[15px] font-bold text-[#123B5D]">{s.title}</div>
                                    <div className="mt-2 text-[13px] text-[#17212B]/70 leading-relaxed">{s.body}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
            )}

            {/* ============================= FAQ ============================= */}
            {showSection("faq") && (
            <section id="faq" className="bg-[#F5F8FA] border-y border-[#123B5D]/10">
                <div className="max-w-4xl mx-auto px-6 py-20 lg:py-24">
                    <div className="text-xs uppercase tracking-widest text-[#2563EB] font-semibold">FAQ</div>
                    <h2 data-reveal data-reveal-delay="40" className="mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-[#123B5D]">Answers before you sign up.</h2>
                    <Accordion type="single" collapsible className="mt-10" data-testid="landing-faq">
                        {(content.faqs && content.faqs.length > 0 ? content.faqs : FAQ).map((f, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#123B5D]/10">
                                <AccordionTrigger className="text-left text-[15px] font-semibold text-[#123B5D] py-5 hover:no-underline" data-testid={`faq-q-${i}`}>
                                    {f.question || f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-[14px] text-[#17212B]/80 leading-relaxed pb-5">
                                    {f.answer || f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
            )}

            {/* ============================= FINAL CTA ============================= */}
            {showSection("final_cta") && (
            <section className="ddi-hero-bg relative overflow-hidden">
                <div className="absolute inset-0 ddi-grid-overlay opacity-25 pointer-events-none" />
                <div className="absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full bg-[#2563EB]/30 blur-3xl pointer-events-none" />
                <div className="max-w-4xl mx-auto px-6 py-20 lg:py-24 text-center text-white relative">
                    <Star className="mx-auto text-[#F59E0B]" size={28} />
                    <h2 data-reveal data-reveal-delay="80" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                        {finalCta?.heading || "Ready to publish a professional identity your patients will trust?"}
                    </h2>
                    <p className="mt-5 text-white/75 text-lg max-w-2xl mx-auto">
                        {finalCta?.body || "Sign up in under two minutes. Complete onboarding at your pace. Publish with dual approval — refined by AI, approved by you."}
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/signup" className="ddi-teal-btn text-base inline-flex items-center gap-2" data-testid="final-cta-primary">
                            {finalCta?.cta_primary || "Create Your Doctor Digital Identity"}
                            <ArrowRight size={16} />
                        </Link>
                        <Link to="/pricing" className="text-sm font-semibold text-white/80 hover:text-white" data-testid="final-cta-secondary">
                            {finalCta?.cta_secondary || "See packages"} →
                        </Link>
                    </div>
                    <div className="mt-6 text-xs text-white/50">
                        {finalCta?.disclaimer || "No credit card required to sign up. Advance payment starts onboarding."}
                    </div>
                </div>
            </section>
            )}

            <MarketingFooter />
            <PlatformSupportChat />
        </div>
    );
}
